<template>
  <SfModal
    v-if="workflowStatusChangeFormVisible"
    footer-align="right"
    class="sf-modal-s"
    @close="close"
  >
    <template #title>
      {{
        I18n.t('workflow_statuses.change_status_modal.title', {
          name: workflowName,
        })
      }}
    </template>

    <template #messages>
      <SfModalMessage v-if="errorMessage" variant="error">
        {{ errorMessage }}
      </SfModalMessage>
    </template>
    <template #body>
      <div class="sf-modal-content">
        <div class="sf-pad-3">
          <div class="sf-grid sf-grid-fluid">
            <form class="sf-form sf-form-full">
              <div class="sf-level sf-below-1">
                <div class="sf-level-item">
                  {{
                    I18n.t('workflow_statuses.change_status_modal.move_from')
                  }}
                </div>
                <div
                  class="
                    sf-level-item sf-label sf-label-rectangular
                    workflow-status__label
                    sf-c-text
                  "
                >
                  {{ currentStatusName }}
                </div>
              </div>
              <div class="sf-level sf-below-1">
                <div class="sf-level-item">
                  {{ I18n.t('workflow_statuses.change_status_modal.move_to') }}
                </div>
                <div class="sf-level-item sf-c-text-darker">
                  <SfDropdown
                    dropdown-id="workflowStatus"
                    :is-open="isScopedDropdownOpen"
                    @dropdown-clicked="handleToggleDropdown"
                    @dropdown-closed="handleCloseDropdown"
                  >
                    <template #trigger>
                      <div class="workflow-status__dropdown--item">
                        {{ selectedStatus.name }}
                      </div>
                    </template>

                    <template #menu>
                      <SfDropdownItem
                        v-for="item in dropdownItems"
                        :key="item.id"
                        :is-disabled="item.disabled"
                        :is-tall="true"
                        @click.stop="statusSelected(item)"
                      >
                        {{ item.name }}
                      </SfDropdownItem>
                    </template>
                  </SfDropdown>
                </div>
              </div>
              <div
                class="
                  sf-row sf-below-3
                  text
                  optional
                  company_workflow_status_message
                "
              >
                <div class="sf-col">
                  <textarea
                    v-model="remark"
                    v-mentionable="mentionUsernames"
                    class="sf-form-input text optional sf-form-textarea"
                    rows="10"
                    cols="30"
                    :placeholder="
                      I18n.t(
                        'workflow_statuses.change_status_modal.optional_message'
                      )
                    "
                  />
                </div>
              </div>
            </form>
          </div>
          <div class="sf-modal-footer sf-level sf-level-right">
            <a class="sf-btn sf-level-item" @click="close">
              {{ I18n.t('confirmation.cancel') }}
            </a>
            <a
              class="sf-btn sf-btn-success sf-level-item"
              :class="saveButtonClass"
              @click="submit"
            >
              <template v-if="processing">
                {{ I18n.t('common.saving') }}
              </template>
              <template v-else>
                {{ I18n.t('common.save') }}
              </template>
            </a>
          </div>
        </div>
      </div>
    </template>
  </SfModal>
</template>

<script>
import { mapState, mapActions } from 'pinia'

import { SfLog } from '@app/adapters/logger'
import communicationApi from '@app/application/communication/sections/communication/api'
import { dropdowns } from '@app/shared/types/dropdown'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import { SfModal, SfModalMessage } from '@app/shared/vue/components/sf-modal'
import SFetch from '@app/utils/sfetch'

import { useDropdown } from '@app/shared/pinia/dropdown'

const WorkflowStatusChangeForm = {
  components: { SfDropdown, SfDropdownItem, SfModal, SfModalMessage },
  props: {
    workflowStatusId: {
      type: Number,
      required: false,
      default: null,
    },
    workflowId: {
      type: Number,
      required: true,
    },
    workflowName: {
      type: String,
      required: true,
    },
    workflowStatuses: {
      type: Array,
      required: true,
    },
    workflowStatusChangeFormVisible: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['closeWorkflowStatusChangeForm', 'setStatusAndHideForm'],
  data() {
    return {
      errorMessage: null,
      processing: false,
      mentionUsernames: [],
      remark: null,
      currentStatusId: null,
    }
  },
  computed: {
    ...mapState(useDropdown, ['currentDropdownId']),
    dropdownItems() {
      return this.visibleWorklofStatuses.map((status) => ({
        ...status,
        disabled: status.id === this.workflowStatusId,
      }))
    },
    visibleWorklofStatuses() {
      return this.workflowStatuses.filter((x) => x.visible)
    },
    selectedStatusId() {
      if (this.currentStatusId) {
        return this.currentStatusId
      } else {
        const index = this.visibleWorklofStatuses.indexOf(this.currentStatus)
        const lastIndex = this.visibleWorklofStatuses.length - 1

        if (index === lastIndex) {
          return this.visibleWorklofStatuses[lastIndex].id
        } else if (index >= 0 && index < lastIndex) {
          return this.visibleWorklofStatuses[index + 1].id
        } else {
          return this.visibleWorklofStatuses[0].id
        }
      }
    },
    currentStatus() {
      return this.workflowStatuses.find((x) => x.id === this.workflowStatusId)
    },
    currentStatusName() {
      return this.currentStatus
        ? this.currentStatus.name
        : I18n.t('workflow_statuses.change_status_modal.no_status')
    },
    selectedStatus() {
      return (
        this.workflowStatuses.find((x) => x.id === this.selectedStatusId) ||
        this.workflowStatuses[0]
      )
    },
    saveButtonClass() {
      return { 'sf-btn-disabled': this.processing }
    },
    url() {
      return (
        Silverfin.Routes.ledger_company_workflow_path(
          Silverfin.currentFirmId,
          Silverfin.currentCompanyId,
          Silverfin.currentLedgerId(),
          this.workflowId
        ) + '/workflow_status'
      )
    },
    isScopedDropdownOpen() {
      return this.currentDropdownId === dropdowns.WORKFLOW_STATUS
    },
  },
  async mounted() {
    try {
      const data = await communicationApi.getMentionUsernames({
        companyId: Silverfin.currentCompanyId,
      })
      this.mentionUsernames = data.usernames
    } catch (e) {
      SfLog.warn('Failed to fetch mention usernames', {
        extra: {
          error: e,
        },
      })
    }
  },
  methods: {
    ...mapActions(useDropdown, ['toggleDropdown', 'closeDropdown']),
    handleCloseDropdown() {
      this.closeDropdown(dropdowns.WORKFLOW_STATUS)
    },
    handleToggleDropdown() {
      this.toggleDropdown(dropdowns.WORKFLOW_STATUS)
    },
    activeClass(item) {
      return item.id === this.workflowStatusId ? 'sf-dim-el' : 'sf-pointer'
    },
    close() {
      this.$emit('closeWorkflowStatusChangeForm')
    },
    statusSelected(statusId) {
      this.closeDropdown(dropdowns.WORKFLOW_STATUS)
      this.currentStatusId = statusId.id
    },
    async submit() {
      this.processing = true

      try {
        await SFetch.json(this.url, {
          method: 'PATCH',
          body: {
            company_workflow_status: {
              workflow_status_id: this.selectedStatusId,
              message: this.remark,
            },
          },
        })

        this.$emit('setStatusAndHideForm', this.selectedStatusId)
        this.remark = null
        this.errorMessage = null
        this.currentStatusId = null
      } catch (error) {
        if (error.body && error.body.error) {
          this.errorMessage = error.body.error
        } else {
          this.errorMessage = error.statusText
        }
      } finally {
        this.processing = false
      }
    },
  },
}

export default WorkflowStatusChangeForm
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.workflow-status__label {
  border-color: -color('snow', 'darker');
}

.workflow-status__dropdown--item {
  width: 200px;
}
</style>
